// frontend/src/hooks/useAuthModal.ts
import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useLocation } from 'react-router-dom';
// Note: savePendingAction and PendingActionType available from '../utils/authRedirectState'

/**
 * Shared hook for managing authentication modals and pending operations.
 *
 * This hook implements a two-step authentication flow:
 * 1. AuthRequiredModal: "You need to sign in" (generic message)
 * 2. LoginModal: Actual sign-in interface
 *
 * Flow:
 * 1. User triggers auth-required feature
 * 2. AuthRequiredModal appears with generic message
 * 3. User clicks "OK" → LoginModal appears
 * 4. User signs in → Pending operation executes
 *
 * @example
 * ```typescript
 * const {
 *   showAuthRequiredModal,
 *   showLoginModal,
 *   requireAuth,
 *   handleOk,
 *   handleLoginSuccess,
 *   handleClose
 * } = useAuthModal();
 *
 * const handleGenerateCollection = () => {
 *   if (!requireAuth(() => performGeneration())) return;
 *   performGeneration();
 * };
 *
 * return (
 *   <>
 *     <AuthRequiredModal
 *       isOpen={showAuthRequiredModal}
 *       onClose={handleClose}
 *       onOk={handleOk}
 *     />
 *     <LoginModal
 *       isOpen={showLoginModal}
 *       onClose={handleClose}
 *       onSuccess={handleLoginSuccess}
 *     />
 *   </>
 * );
 * ```
 */
export interface UseAuthModalReturn {
  /** Whether the current user is authenticated */
  isAuthenticated: boolean;

  /** Whether the auth required modal should be shown (step 1) */
  showAuthRequiredModal: boolean;

  /** Whether the login modal should be shown (step 2) */
  showLoginModal: boolean;

  /**
   * Check authentication and store operation if not authenticated.
   * Returns true if authenticated (proceed with operation), false if modal was shown.
   *
   * @param operation - Function to execute after successful login
   * @returns true if authenticated, false if auth required modal was shown
   */
  requireAuth: (operation: () => void) => boolean;

  /**
   * Handler for AuthRequiredModal onOk prop.
   * Closes auth required modal and opens login modal.
   */
  handleOk: () => void;

  /**
   * Handler for LoginModal onSuccess prop.
   * Executes pending operation and closes modal.
   */
  handleLoginSuccess: () => void;

  /**
   * Handler for modal close (both AuthRequired and Login).
   * Clears pending operation and closes all modals.
   */
  handleClose: () => void;
}

export const useAuthModal = (): UseAuthModalReturn => {
  const { isAuthenticated } = useAuth();
  useLocation();
  const [showAuthRequiredModal, setShowAuthRequiredModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingOperation, setPendingOperation] = useState<(() => void) | null>(null);

  // Save current URL before redirect (when login modal opens)
  useEffect(() => {
    if (showLoginModal) {
      // Save the current URL so we can return here after OAuth redirect
      const returnUrl = window.location.pathname + window.location.search + window.location.hash;
      sessionStorage.setItem('aiprep_auth_return_url', returnUrl);
      console.log('[useAuthModal] Saved return URL:', returnUrl);
    }
  }, [showLoginModal]);

  /**
   * Check authentication before performing an operation.
   * If not authenticated, stores the operation and shows auth required modal (step 1).
   * If already authenticated, returns true to allow immediate execution.
   */
  const requireAuth = useCallback((operation: () => void): boolean => {
    if (!isAuthenticated) {
      // Warn if overwriting existing pending operation
      if (pendingOperation !== null) {
        console.warn(
          '⚠️ useAuthModal: Overwriting existing pending operation. ' +
          'Previous operation will be lost. This may indicate multiple auth-required ' +
          'actions triggered before login completed.'
        );
      }

      // Store operation to execute after login
      setPendingOperation(() => operation);
      // Show auth required modal first (step 1)
      setShowAuthRequiredModal(true);
      return false;
    }

    // Already authenticated, proceed immediately
    return true;
  }, [isAuthenticated, pendingOperation]);

  /**
   * User clicked "OK" in AuthRequiredModal.
   * Close auth required modal and open login modal (step 2).
   */
  const handleOk = useCallback(() => {
    setShowAuthRequiredModal(false);
    setShowLoginModal(true);
  }, []);

  /**
   * Handle successful login.
   * Executes the pending operation if one exists, then cleans up.
   */
  const handleLoginSuccess = useCallback(async () => {
    setShowLoginModal(false);

    if (pendingOperation) {
      try {
        // Execute the pending operation (await to handle both sync and async operations)
        await Promise.resolve(pendingOperation());
      } catch (error) {
        console.error('useAuthModal: Failed to execute pending operation after login:', error);
        // Don't rethrow - let the operation's own error handling deal with it
      } finally {
        // Always clear pending operation after execution
        setPendingOperation(null);
      }
    }
  }, [pendingOperation]);

  /**
   * Handle modal close (user clicked X or clicked outside).
   * Closes both modals and clears pending operation.
   */
  const handleClose = useCallback(() => {
    setShowAuthRequiredModal(false);
    setShowLoginModal(false);
    setPendingOperation(null);
  }, []);

  return {
    isAuthenticated,
    showAuthRequiredModal,
    showLoginModal,
    requireAuth,
    handleOk,
    handleLoginSuccess,
    handleClose,
  };
};
